import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { setErrorNotifier } from "@/app/query/query-client";
import type { ApiErrorCode } from "@shared/contracts";

/**
 * ONE MESSAGE PER FAILURE, FOR THE WHOLE APP.
 *
 * ⚠️ A DECISION, NOT A FOUND PATTERN. The design system's capture has no transient notice
 * (field 12.25 is on its "not decided" list), so this is built from its own tokens rather
 * than invented in a different visual language: the surface, the radius alias and the type
 * role are all the system's. If the system later ships one, replace this and keep where the
 * message comes from — the query cache is the single authority, and screens do not each
 * decide how to announce a failure.
 *
 * The text is looked up from the error CODE, so one vocabulary is translated once.
 */
export function ErrorToast() {
  const { t } = useTranslation();
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    setErrorNotifier((code: ApiErrorCode) => {
      setMessage(t(`errors.${code}`));
      // Long enough to read on a phone held at arm's length, short enough not to sit over
      // the thing the user is trying to tap.
      window.setTimeout(() => setMessage(null), 4000);
    });
    return () => setErrorNotifier(() => {});
  }, [t]);

  if (!message) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 z-50 flex justify-center px-4"
      style={{ bottom: "calc(5rem + var(--safe-bottom) + var(--keyboard-height))" }}
    >
      <p className="rounded-pill bg-foreground text-background text-support px-4 py-2 text-center">
        {message}
      </p>
    </div>
  );
}
